
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  image: string;
  readTime: string;
}

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'industry', name: 'Industry News' },
    { id: 'sustainability', name: 'Sustainability' },
    { id: 'farming', name: 'Farming Practices' },
    { id: 'market', name: 'Market Insights' },
    { id: 'company', name: 'Company News' }
  ];

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: "The Future of Sustainable Cocoa Farming in Nigeria",
      excerpt: "Exploring innovative approaches to sustainable cocoa production that benefit both farmers and the environment while meeting global demand for ethical chocolate.",
      content: "Nigeria's cocoa industry stands at a pivotal moment. As global demand for sustainable and ethically sourced cocoa continues to rise, Nigerian farmers and exporters are embracing innovative practices that promise to transform the industry. At Olatunde International Limited, we've witnessed firsthand the positive impact of sustainable farming practices on both yield quality and farmer livelihoods. Our partnership with over 5,000 farmers across Nigeria's cocoa belt has demonstrated that sustainability isn't just an environmental imperative—it's a business opportunity. Through our comprehensive farmer training programs, we've seen average yields increase by 35% while reducing environmental impact. The integration of agroforestry practices, climate-smart agriculture, and digital traceability systems is creating a new paradigm for cocoa production. These initiatives not only improve the quality of cocoa beans but also enhance biodiversity, improve soil health, and provide additional income streams for farming families. As we look toward the future, the collaboration between exporters, farmers, and international buyers will be crucial in building a truly sustainable cocoa value chain that benefits all stakeholders.",
      author: "Dr. Adebayo Olatunde",
      date: "March 15, 2024",
      category: "sustainability",
      image: "https://readdy.ai/api/search-image?query=sustainable%20cocoa%20farming%20in%20Nigeria%20with%20farmers%20working%20in%20lush%20green%20cocoa%20plantations%2C%20modern%20agricultural%20practices%2C%20solar%20panels%2C%20and%20eco-friendly%20farming%20equipment%20under%20bright%20African%20sunlight&width=800&height=400&seq=blog1&orientation=landscape",
      readTime: "8 min read"
    },
    {
      id: 2,
      title: "Global Cocoa Market Trends: What Exporters Need to Know",
      excerpt: "An in-depth analysis of current global cocoa market dynamics, price trends, and emerging opportunities for Nigerian cocoa exporters in international markets.",
      content: "The global cocoa market is experiencing unprecedented changes driven by evolving consumer preferences, sustainability requirements, and supply chain innovations. Recent market analysis reveals a growing premium for certified sustainable cocoa, with prices for Rainforest Alliance and Fairtrade certified beans commanding 15-20% higher prices than conventional cocoa. European and North American chocolate manufacturers are increasingly prioritizing traceability and ethical sourcing, creating significant opportunities for Nigerian exporters who can demonstrate compliance with international standards. The rise of bean-to-bar chocolate makers has also created demand for unique flavor profiles and origin-specific cocoa, areas where Nigeria's diverse growing regions excel. Climate change concerns are reshaping long-term market dynamics, with buyers seeking suppliers who can demonstrate resilience and adaptation strategies. Digital technologies are revolutionizing supply chain transparency, enabling real-time tracking from farm to factory. For Nigerian exporters, these trends represent both challenges and opportunities. Those who invest in certification, traceability systems, and direct farmer relationships are positioned to capture premium market segments and build long-term partnerships with international buyers.",
      author: "Sarah Adebisi",
      date: "March 10, 2024",
      category: "market",
      image: "https://readdy.ai/api/search-image?query=global%20cocoa%20market%20analysis%20with%20charts%2C%20graphs%2C%20world%20map%20showing%20trade%20routes%2C%20cocoa%20beans%2C%20and%20modern%20trading%20floor%20with%20digital%20displays%20showing%20market%20data&width=800&height=400&seq=blog2&orientation=landscape",
      readTime: "6 min read"
    },
    {
      id: 3,
      title: "Empowering Women in Nigeria's Cocoa Value Chain",
      excerpt: "Highlighting the crucial role of women in cocoa farming and our initiatives to promote gender equality and economic empowerment in rural communities.",
      content: "Women constitute approximately 60% of the labor force in Nigeria's cocoa sector, yet they often lack equal access to resources, training, and decision-making opportunities. At Olatunde International Limited, we recognize that empowering women is not just a moral imperative—it's essential for the sustainable growth of the cocoa industry. Our Women in Cocoa Initiative has reached over 2,000 women farmers across Ondo, Osun, and Oyo states, providing specialized training in post-harvest processing, financial literacy, and cooperative management. The results have been remarkable: participating women have increased their income by an average of 45% and have taken leadership roles in their communities. We've established women-only cooperatives that focus on value-added activities such as cocoa butter production and organic certification. These cooperatives have become models for other regions, demonstrating how gender-inclusive approaches can improve both social outcomes and business performance. Our partnership with international development organizations has enabled us to provide microfinance services, healthcare support, and educational scholarships for the children of women farmers. As we continue to expand these programs, we're seeing a transformation in rural communities where women are becoming agents of change, driving innovation and sustainability in cocoa production.",
      author: "Folake Adeyemi",
      date: "March 5, 2024",
      category: "sustainability",
      image: "https://readdy.ai/api/search-image?query=empowered%20African%20women%20farmers%20working%20in%20cocoa%20plantations%2C%20women%20in%20colorful%20traditional%20clothing%20processing%20cocoa%20beans%2C%20community%20meeting%20with%20women%20leaders%2C%20bright%20and%20uplifting%20atmosphere&width=800&height=400&seq=blog3&orientation=landscape",
      readTime: "7 min read"
    },
    {
      id: 4,
      title: "Technology Revolution in Cocoa Traceability",
      excerpt: "How blockchain and IoT technologies are transforming cocoa supply chain transparency and enabling better farmer support and premium pricing.",
      content: "The cocoa industry is undergoing a digital transformation that promises to revolutionize how we track, verify, and optimize the journey from farm to chocolate bar. At Olatunde International Limited, we've invested heavily in cutting-edge traceability technologies that provide unprecedented visibility into our supply chain. Our blockchain-based system records every transaction and quality checkpoint, creating an immutable record that buyers can trust. IoT sensors in our warehouses monitor temperature, humidity, and other critical factors that affect bean quality, while mobile applications enable farmers to record harvest data in real-time. This technological infrastructure has enabled us to achieve 100% traceability for our premium product lines, meeting the stringent requirements of European and North American buyers. The benefits extend beyond compliance: our data analytics platform helps farmers optimize their practices, predict optimal harvest times, and access market information that enables better pricing decisions. Smart contracts automate payments to farmers, reducing delays and ensuring fair compensation. As we continue to expand our technology platform, we're exploring applications of artificial intelligence for quality prediction and satellite imagery for monitoring farm conditions. These innovations are not just improving our operations—they're setting new standards for the entire industry.",
      author: "Eng. Tunde Ogundimu",
      date: "February 28, 2024",
      category: "industry",
      image: "https://readdy.ai/api/search-image?query=modern%20technology%20in%20agriculture%20with%20blockchain%20visualization%2C%20IoT%20sensors%20in%20cocoa%20farms%2C%20digital%20tablets%20showing%20data%20analytics%2C%20futuristic%20farming%20technology%20with%20clean%20modern%20design&width=800&height=400&seq=blog4&orientation=landscape",
      readTime: "9 min read"
    },
    {
      id: 5,
      title: "Climate-Smart Agriculture: Adapting to Changing Conditions",
      excerpt: "Strategies and techniques we're implementing to help cocoa farmers adapt to climate change while maintaining productivity and quality.",
      content: "Climate change poses significant challenges to cocoa production in Nigeria, with changing rainfall patterns, increased temperatures, and extreme weather events affecting yields and quality. However, through climate-smart agriculture practices, we're helping farmers not just adapt but thrive in this changing environment. Our comprehensive climate adaptation program includes drought-resistant cocoa varieties, improved irrigation systems, and agroforestry techniques that provide natural climate regulation. We've introduced shade trees that not only protect cocoa plants from extreme heat but also provide additional income through fruit and timber production. Soil conservation practices, including cover cropping and organic matter enhancement, are improving soil health and water retention capacity. Our weather monitoring network provides farmers with real-time data and early warning systems for adverse weather conditions. Training programs focus on integrated pest management, as changing climate conditions affect pest and disease patterns. The results have been encouraging: participating farmers have maintained stable yields despite challenging weather conditions, while also improving soil health and biodiversity. Carbon sequestration through our agroforestry initiatives is generating additional revenue through carbon credit programs. As we scale these practices across our network, we're demonstrating that climate adaptation and economic prosperity can go hand in hand.",
      author: "Dr. Kemi Adebayo",
      date: "February 20, 2024",
      category: "farming",
      image: "https://readdy.ai/api/search-image?query=climate-smart%20cocoa%20farming%20with%20shade%20trees%2C%20modern%20irrigation%20systems%2C%20farmers%20using%20weather%20monitoring%20equipment%2C%20lush%20green%20sustainable%20agriculture%20landscape%20with%20diverse%20crops&width=800&height=400&seq=blog5&orientation=landscape",
      readTime: "8 min read"
    },
    {
      id: 6,
      title: "Olatunde International Expands Operations to Cross River State",
      excerpt: "Announcing our expansion into Cross River State's emerging cocoa region, bringing our sustainable sourcing model to new farming communities.",
      content: "We're excited to announce the expansion of Olatunde International Limited's operations into Cross River State, marking a significant milestone in our growth strategy and commitment to supporting Nigeria's cocoa farmers. This expansion represents our largest investment in new territory since our founding, with plans to establish three collection centers and partner with over 1,000 farmers in the region. Cross River State's unique climate and soil conditions produce cocoa with distinctive flavor characteristics that are highly sought after by specialty chocolate manufacturers. Our entry into this market brings our proven sustainable sourcing model to a region that has tremendous potential but has historically lacked access to premium markets. The expansion includes the establishment of a regional training center that will provide farmers with access to best practices in sustainable farming, post-harvest processing, and quality management. We're partnering with local cooperatives and community leaders to ensure our programs are culturally appropriate and community-driven. Our investment includes infrastructure development such as improved rural roads and storage facilities that will benefit the entire region. The Cross River expansion is expected to increase our sourcing capacity by 30% while providing new opportunities for farmer development and community empowerment. This growth reflects our confidence in Nigeria's cocoa sector and our commitment to building a truly national network of sustainable cocoa production.",
      author: "CEO Office",
      date: "February 15, 2024",
      category: "company",
      image: "https://readdy.ai/api/search-image?query=business%20expansion%20celebration%20with%20Nigerian%20cocoa%20farmers%2C%20new%20modern%20collection%20center%20building%2C%20company%20executives%20meeting%20with%20local%20community%20leaders%2C%20professional%20and%20optimistic%20atmosphere&width=800&height=400&seq=blog6&orientation=landscape",
      readTime: "5 min read"
    }
  ];

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const openPost = (post: BlogPost) => {
    setSelectedPost(post);
    document.body.style.overflow = 'hidden';
  };

  const closePost = () => {
    setSelectedPost(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-800 to-green-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Industry Insights & News
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Stay updated with the latest developments in sustainable cocoa farming, 
            market trends, and our journey toward building a better cocoa value chain.
          </p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-amber-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-amber-50 border border-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => openPost(post)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
                      {categories.find(cat => cat.id === post.category)?.name}
                    </span>
                    <span className="text-gray-500 text-sm">{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-user-line text-green-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{post.author}</p>
                        <p className="text-xs text-gray-500">{post.date}</p>
                      </div>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-amber-600"></i>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Post Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 pr-4">
                {selectedPost.title}
              </h2>
              <button
                onClick={closePost}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="aspect-video overflow-hidden rounded-lg mb-6">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
                    {categories.find(cat => cat.id === selectedPost.category)?.name}
                  </span>
                  <span className="text-gray-500 text-sm">{selectedPost.readTime}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-user-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedPost.author}</p>
                    <p className="text-xs text-gray-500">{selectedPost.date}</p>
                  </div>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedPost.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
